import { Input, Label, Field, Radio, RadioGroup } from '@headlessui/react';
import { faCheckCircle, faChevronUp, faMinusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import {
  ITripFee,
  TripFeeRuleEnums,
  TripFeeTypeEnums,
  TripFeeUniqNameEnums,
} from '../../../__generated__/graphql';
import SwitchComponent from '../../components/switch';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { MoonLoader, SquareLoader } from 'react-spinners';
import {
  CREATE_TRIP_FEE,
  GET_TRIP_FEES,
  UPDATE_TRIP_FEE,
} from '../../services/tripFeeServices/queries';
import {
  ICreateTripFeeMutation,
  ICreateTripFeeMutationVariables,
  IGetTripFeesQuery,
  IUpdateTripFeeMutationVariables,
  IUpdateTripFeeMutation,
} from '../../services/tripFeeServices/__generated__/queries.generated';
import { authStore } from '../../../store/authStore';
import { getBrand } from '../../constants/brands';
import { Alert } from '../../components/ui';
const PageContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 box-border px-4 pb-8 md:pb-16`}
`;

const ContentContainer = styled.div`
  ${tw`mx-auto w-full max-w-screen-xl max-w-full min-w-0 box-border`}
`;

const StyledRadioGroup = styled(RadioGroup)`
  ${tw`space-y-2`}
`;

const StyledRadio = styled(Radio)<{ $warning?: boolean }>`
  ${tw`relative flex cursor-pointer rounded-lg py-4 px-5 text-white shadow-card transition`}
  ${({ $warning }) => ($warning ? tw`bg-amber-100` : tw`bg-bg-secondary`)}
  &:focus {
    ${tw`outline-1 outline-red-500`}
  }
  &[data-checked] {
    ${({ $warning }) => ($warning ? tw`bg-amber-200/80` : tw`bg-black/10`)}
  }
`;

const MonthlyInactiveWarning = styled.p`
  ${tw`text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mt-2`}
`;

const RadioContent = styled.div`
  ${tw`flex w-full items-center justify-between`}
`;

const PlanInfo = styled.div`
  ${tw`text-sm/6`}
`;

const PlanName = styled.p`
  ${tw`font-semibold text-text-primary`}
`;

const PlanSubtitle = styled.p`
  ${tw`text-xs text-text-secondary mt-1`}
`;

const PlanDetails = styled.div`
  ${tw`flex gap-2 text-text-primary`}
`;

const Separator = styled.div`
  &::before {
    content: '·';
  }
`;

const StyledLabel = styled(Label)`
  ${tw`text-sm/6 font-medium text-text-primary`}
`;

const StyledInput = styled.input`
  ${tw`mt-3 w-full rounded-lg border-none bg-bg-secondary text-sm/6 text-text-primary h-[44px]`}
  &:focus {
    ${tw`outline-none`}
  }
  &[data-focus] {
    ${tw`outline-2 -outline-offset-2 outline-white/25`}
  }
`;

const StyledArea = styled.textarea`
  ${tw`max-w-full h-[300px]`}
`;

const StyledDescriptionArea = styled.textarea`
  ${tw`max-w-full h-[100px]`}
`;

const InputField = styled(Field)<{ inVertical?: boolean }>`
  ${tw`max-w-full bg-black/10 p-4 flex flex-col`}
  ${({ inVertical = true }) => !inVertical && tw`flex flex-row justify-between`}
`;

const InputContainer = styled.div`
  ${tw`max-w-full mt-4 bg-bg-secondary`}
`;

const DeSelectButton = styled.button`
  ${tw`w-full h-[30px] border-none bg-black/10`}
  &:hover {
    ${tw`bg-black/20 cursor-pointer`}
  }
`;

const StyledDefaultLabel = styled.p`
  ${tw`text-sm/6 font-medium text-text-primary italic`}
`;

const StyledTitle = styled.h1`
  ${tw`text-3xl font-bold text-text-primary mt-10 mb-6`}
`;
const defaultTripFees: Partial<ITripFee>[] = [
  {
    uniqName: TripFeeUniqNameEnums.Tripfee,
    name: 'Trip Fee',
    chargeBy: TripFeeTypeEnums.DailyRate,
    amount: 12,
    rule: TripFeeRuleEnums.Charge,
    description: '',
    isActive: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    uniqName: TripFeeUniqNameEnums.Youngsterfee18,
    name: '<21 Youngster Fee',
    chargeBy: TripFeeTypeEnums.DailyRate,
    amount: 0,
    rule: TripFeeRuleEnums.Charge,
    description: '',
    isActive: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    uniqName: TripFeeUniqNameEnums.Youngsterfee21,
    name: '21-25 Youngster Fee',
    chargeBy: TripFeeTypeEnums.DailyRate,
    amount: 0,
    rule: TripFeeRuleEnums.Charge,
    description: '',
    isActive: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    uniqName: TripFeeUniqNameEnums.MonthlyTripfee,
    name: 'Monthly Trip Fee',
    chargeBy: TripFeeTypeEnums.DailyRate,
    amount: 0,
    rule: TripFeeRuleEnums.Charge,
    description: 'Trip fee applied to MONTHLY rentals only',
    isActive: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    uniqName: TripFeeUniqNameEnums.MonthlyYoungsterfee18,
    name: 'Monthly <21 Youngster Fee',
    chargeBy: TripFeeTypeEnums.DailyRate,
    amount: 15,
    rule: TripFeeRuleEnums.Charge,
    description: 'Youngster fee for under-21 drivers on MONTHLY rentals only',
    isActive: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    uniqName: TripFeeUniqNameEnums.MonthlyYoungsterfee21,
    name: 'Monthly 21-25 Youngster Fee',
    chargeBy: TripFeeTypeEnums.DailyRate,
    amount: 10,
    rule: TripFeeRuleEnums.Charge,
    description: 'Youngster fee for 21-25 drivers on MONTHLY rentals only',
    isActive: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    uniqName: TripFeeUniqNameEnums.FreeCancellationGracePeriod,
    name: 'free cancellation grace period',
    amount: 1,
    rule: TripFeeRuleEnums.AdvanceRules,
    description: '',
    isActive: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    uniqName: TripFeeUniqNameEnums.FreeCancellationAdvance,
    name: 'free cancellation advance in hours',
    amount: 24,
    rule: TripFeeRuleEnums.AdvanceRules,
    description: '',
    isActive: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    uniqName: TripFeeUniqNameEnums.CancellationMaxPenalty,
    name: 'Cancellation Maximum Penalty',
    chargeBy: TripFeeTypeEnums.OneTime,
    rule: TripFeeRuleEnums.Charge,
    amount: 50,
    description: '',
    isActive: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    uniqName: TripFeeUniqNameEnums.ShortenBookingMaxPenalty,
    name: 'shorten booking max penalty',
    chargeBy: TripFeeTypeEnums.Percentage,
    rule: TripFeeRuleEnums.Charge,
    amount: 50,
    description: '',
    isActive: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    uniqName: TripFeeUniqNameEnums.FreeCancellationServiceFee,
    name: 'Free Cancellation or Shorten Service Fee',
    chargeBy: TripFeeTypeEnums.Percentage,
    rule: TripFeeRuleEnums.Charge,
    amount: 0,
    description: '',
    isActive: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

const StyledButton = styled.button`
  ${tw`gap-10 w-full cursor-pointer rounded-lg bg-black px-4 py-3 text-center text-sm font-semibold text-white shadow-xs transition duration-200 ease-in-out border-none`}
  &:hover {
    ${tw`bg-green-700`}
  }
`;

const StyledSelect = styled.select`
  ${tw`w-full h-[44px]`}
`;

const TabBar = styled.div`
  ${tw`flex bg-bg-secondary rounded-lg p-1 mb-6 max-w-sm`}
`;

const TabButton = styled.button<{ active: boolean }>`
  ${tw`flex-1 py-2 rounded-md font-medium transition-colors border-none cursor-pointer`}
  ${({ active }) =>
    active ? tw`bg-white text-text-primary shadow-xs` : tw`bg-transparent text-text-secondary`}
`;

const MONTHLY_FEE_UNIQ_NAMES = [
  TripFeeUniqNameEnums.MonthlyTripfee,
  TripFeeUniqNameEnums.MonthlyYoungsterfee18,
  TripFeeUniqNameEnums.MonthlyYoungsterfee21,
] as const;

function isMonthlyTripFee(uniqName?: string | null): boolean {
  return MONTHLY_FEE_UNIQ_NAMES.includes(uniqName as (typeof MONTHLY_FEE_UNIQ_NAMES)[number]);
}

function showMonthlyInactiveWarning(fee: ITripFee, mode: 'daily' | 'monthly'): boolean {
  return mode === 'monthly' && isMonthlyTripFee(fee.uniqName) && !(fee.id && fee.isActive);
}

function monthlyInactiveWarningMessage(feeName: string): string {
  const label = feeName.trim() || 'This fee';
  return `${label} is not active, so monthly rental is off on all your cars. Trips of 30 days or more are priced as daily trips, with your daily prices, discounts, fees and protection plans. To offer monthly rental, set this fee to Active (the amount can be $0).`;
}

/**
 * Spec 014 Phase 3: cancellation and shortening amounts come from the brand's
 * cancellation rules (admin Brand settings), so these rows no longer apply and
 * are not shown.
 */
export const CANCELLATION_FEE_UNIQ_NAMES: readonly string[] = [
  TripFeeUniqNameEnums.FreeCancellationGracePeriod,
  TripFeeUniqNameEnums.FreeCancellationAdvance,
  TripFeeUniqNameEnums.CancellationMaxPenalty,
  TripFeeUniqNameEnums.ShortenBookingMaxPenalty,
  TripFeeUniqNameEnums.FreeCancellationServiceFee,
];

const DAILY_BOOKING_FEE_UNIQ_NAMES = [
  TripFeeUniqNameEnums.Tripfee,
  TripFeeUniqNameEnums.Youngsterfee18,
  TripFeeUniqNameEnums.Youngsterfee21,
] as const;

export default function TripFeePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tripFees, setTripFees] = useState<ITripFee[] | typeof defaultTripFees>(defaultTripFees);
  const [selected, setSelected] = useState<ITripFee | null>(null);
  const [mode, setMode] = useState<'daily' | 'monthly'>('daily');

  const userId = authStore.user?.id;

  useEffect(() => {
    const tab = searchParams.get('tab') ?? searchParams.get('mode');
    if (tab === 'monthly') {
      setMode('monthly');
    } else if (tab === 'daily') {
      setMode('daily');
    }
  }, [searchParams]);

  const { data, loading, error, refetch } = useQuery<IGetTripFeesQuery>(GET_TRIP_FEES, {
    variables: {
      userId: userId,
    },
    skip: !userId, // Skip query if userId is not available
  });

  // Sync tripFees state whenever data changes (including after refetch)
  useEffect(() => {
    if (data?.tripFees) {
      const updatedTripFees = defaultTripFees.map(fee => {
        const matchedFee = data.tripFees.find(
          p => p.uniqName === fee.uniqName || p.name === fee.name,
        );
        if (matchedFee) {
          return matchedFee;
        }
        return fee;
      });
      setTripFees(updatedTripFees as ITripFee[]);
    }
  }, [data]);

  if (loading) return <MoonLoader color="green" />;
  if (error) return <div>Error: {error.message}</div>;

  // Tab filtering:
  // - Daily tab: shared rules + DAILY-only TRIPFEE/YOUNGSTERFEE variants.
  // - Monthly tab: shared rules + MONTHLY-only TRIPFEE/YOUNGSTERFEE variants.
  const visibleFees = (tripFees as ITripFee[]).filter(fee => {
    if (CANCELLATION_FEE_UNIQ_NAMES.includes(fee.uniqName as string)) {
      return false;
    }
    if (mode === 'daily') {
      return !MONTHLY_FEE_UNIQ_NAMES.includes(fee.uniqName as any);
    }
    return !DAILY_BOOKING_FEE_UNIQ_NAMES.includes(fee.uniqName as any);
  });

  return (
    <PageContainer>
      <ContentContainer>
        <StyledTitle>Trip Fees Set up</StyledTitle>
        <TabBar>
          <TabButton
            type="button"
            active={mode === 'daily'}
            onClick={() => {
              setSelected(null);
              setMode('daily');
              setSearchParams({ tab: 'daily' }, { replace: true });
            }}
          >
            Daily
          </TabButton>
          <TabButton
            type="button"
            active={mode === 'monthly'}
            onClick={() => {
              setSelected(null);
              setMode('monthly');
              setSearchParams({ tab: 'monthly' }, { replace: true });
            }}
          >
            Monthly
          </TabButton>
        </TabBar>
        <Alert tone="info" title="Cancellation fees moved">
          {`${getBrand().name} sets the cancellation and shortening fees for every host on this site. The cancellation rows that used to be here no longer apply.`}
        </Alert>
        <StyledRadioGroup value={selected} aria-label="Server size" as="div">
          {visibleFees.map(fee => (
            <div key={fee.name}>
              <EachPlan
                fee={fee as ITripFee}
                mode={mode}
                isSelected={selected?.name === fee.name}
                onDeselect={() => setSelected(null)}
                setSelected={setSelected}
                userId={userId}
              />
            </div>
          ))}
        </StyledRadioGroup>
      </ContentContainer>
    </PageContainer>
  );
}

export const EachPlan = ({
  fee,
  mode,
  isSelected,
  onDeselect,
  setSelected,
  userId,
}: {
  fee: ITripFee;
  mode: 'daily' | 'monthly';
  isSelected: boolean;
  onDeselect: () => void;
  setSelected: Dispatch<SetStateAction<ITripFee | null>>;
  userId?: string;
}) => {
  const showWarning = showMonthlyInactiveWarning(fee, mode);
  const [isActive, setIsActive] = useState(!!fee.isActive);
  const [amount, setAmount] = useState(fee.amount ?? 0);
  const [description, setDescription] = useState(fee.description || '');
  const [chargeBy, setChargeBy] = useState(
    fee.chargeBy || (fee.rule === TripFeeRuleEnums.Charge ? TripFeeTypeEnums.DailyRate : undefined),
  );
  const [rule, setRule] = useState(fee.rule || undefined);
  useEffect(() => {
    setIsActive(!!fee.isActive);
    setAmount(fee.amount || 0);
    setDescription(fee.description || '');
    fee.chargeBy && setChargeBy(fee.chargeBy);
    fee.rule && setRule(fee.rule);
  }, [fee]);

  const [createTripFee, { loading: isCreating }] = useMutation<
    ICreateTripFeeMutation,
    ICreateTripFeeMutationVariables
  >(CREATE_TRIP_FEE, {
    refetchQueries: [GET_TRIP_FEES],
    onCompleted: () => {
      toast.success('Trip fee created successfully');
    },
    onError: () => {
      toast.error('Failed to create trip fee');
    },
  });

  const [updateTripFee, { loading: isUpdating }] = useMutation<
    IUpdateTripFeeMutation,
    IUpdateTripFeeMutationVariables
  >(UPDATE_TRIP_FEE, {
    refetchQueries: [GET_TRIP_FEES],
    onCompleted: () => {
      toast.success('Trip fee updated successfully');
    },
    onError: () => {
      toast.error('Failed to update trip fee');
    },
  });
  const handleSave = () => {
    if (!userId) {
      toast.error('User ID is required');
      return;
    }
    const input: ICreateTripFeeMutationVariables['input'] = {
      uniqName: fee.uniqName,
      name: fee.name,
      amount: amount,
      description: description,
      isActive: isActive,
      rule: rule || fee.rule,
      userId,
    };
    if (chargeBy) {
      input.chargeBy = chargeBy;
    }
    if (!fee.id) {
      createTripFee({
        variables: {
          input,
        },
      });
    } else {
      updateTripFee({
        variables: {
          input: {
            id: fee.id,
            ...input,
          },
        },
      });
    }
  };

  const getChargeLabel = () => {
    if (rule === TripFeeRuleEnums.AdvanceRules) {
      return `should be ___ hours before trip?`;
    }
    if (rule === TripFeeRuleEnums.Charge) {
      return `amount:`;
    }

    if (rule === TripFeeRuleEnums.Refund) {
      return `${fee.amount ?? 0}%`;
    }
  };

  const renderChargingText = () => {
    if (rule === TripFeeRuleEnums.Charge) {
      if (chargeBy === TripFeeTypeEnums.DailyRate) {
        return `${fee.amount ?? 0}/day`;
      }
      if (chargeBy === TripFeeTypeEnums.OneTime) {
        return `${fee.amount ?? 0}/one time`;
      }
      if (chargeBy === TripFeeTypeEnums.Percentage) {
        return `${fee.amount ?? 0}%`;
      }
    }
    if (rule === TripFeeRuleEnums.AdvanceRules) {
      if (fee.uniqName === TripFeeUniqNameEnums.FreeCancellationGracePeriod) {
        return `within ${fee.amount ?? 0} hour booking`;
      }
      if (fee.uniqName === TripFeeUniqNameEnums.FreeCancellationAdvance) {
        return `${fee.amount ?? 0} hours before trip`;
      }
      return `${fee.amount ?? 0} hours before trip`;
    }
    if (rule === TripFeeRuleEnums.Refund) {
      return `${fee.amount ?? 0} refund`;
    }
  };

  const renderDefaultChargingText = () => {
    switch (fee.uniqName) {
      case TripFeeUniqNameEnums.Tripfee:
        return <StyledDefaultLabel>default: $0/day</StyledDefaultLabel>;
      case TripFeeUniqNameEnums.Youngsterfee18:
        return <StyledDefaultLabel>default: $15/day</StyledDefaultLabel>;
      case TripFeeUniqNameEnums.Youngsterfee21:
        return <StyledDefaultLabel>default: $25/day</StyledDefaultLabel>;
      case TripFeeUniqNameEnums.CancellationMaxPenalty:
        return <StyledDefaultLabel>default: $50</StyledDefaultLabel>;
      case TripFeeUniqNameEnums.ShortenBookingMaxPenalty:
        return <StyledDefaultLabel>default: 50% per day</StyledDefaultLabel>;
      case TripFeeUniqNameEnums.FreeCancellationGracePeriod:
        return <StyledDefaultLabel>default: 1 hour</StyledDefaultLabel>;
      case TripFeeUniqNameEnums.FreeCancellationAdvance:
        return <StyledDefaultLabel>default: 24 hours</StyledDefaultLabel>;
      case TripFeeUniqNameEnums.FreeCancellationServiceFee:
        return <StyledDefaultLabel>default: 0% (no fee)</StyledDefaultLabel>;
    }
  };

  return (
    <>
      <StyledRadio
        key={fee.name}
        value={fee}
        $warning={showWarning}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setSelected(fee);
        }}
        as="div"
      >
        <RadioContent as="div">
          <PlanInfo>
            <PlanName>{fee.name}</PlanName>
            {fee.uniqName === TripFeeUniqNameEnums.FreeCancellationServiceFee && (
              <PlanSubtitle>
                E.g., 3% means a $3000 refund becomes $2910 (to recover Stripe processing fees)
              </PlanSubtitle>
            )}
            <PlanDetails>
              {renderChargingText()}
              <Separator aria-hidden="true" />
              <div>
                updated on:{' '}
                {fee.updatedAt
                  ? new Date(fee.updatedAt).toLocaleDateString()
                  : new Date(fee.createdAt).toLocaleDateString()}
              </div>
              <Separator aria-hidden="true" />
              <div>{fee.id && fee.isActive ? 'Active' : 'Inactive'}</div>
            </PlanDetails>
            {renderDefaultChargingText()}
          </PlanInfo>
          {fee.id && fee.isActive ? (
            <FontAwesomeIcon icon={faCheckCircle} color="green" />
          ) : (
            <FontAwesomeIcon icon={faMinusCircle} color="red" />
          )}
        </RadioContent>
      </StyledRadio>
      {showWarning ? (
        <MonthlyInactiveWarning>{monthlyInactiveWarningMessage(fee.name)}</MonthlyInactiveWarning>
      ) : null}
      {isSelected ? (
        <InputContainer>
          <DeSelectButton onClick={onDeselect}>
            <FontAwesomeIcon icon={faChevronUp} size="xl" color="darkgreen" />
          </DeSelectButton>
          <InputField inVertical={false}>
            <StyledLabel>Active</StyledLabel>
            <SwitchComponent
              isActive={!!isActive}
              onChange={() => setIsActive(!isActive)}
              color="green"
            />
          </InputField>
          {rule !== TripFeeRuleEnums.AdvanceRules ? (
            <InputField>
              <StyledSelect
                value={chargeBy}
                onChange={e => setChargeBy(e.target.value as TripFeeTypeEnums)}
              >
                <option value={TripFeeTypeEnums.DailyRate}>Daily Rate</option>
                <option value={TripFeeTypeEnums.OneTime}>One Time</option>
                <option value={TripFeeTypeEnums.Percentage}>Percentage</option>
              </StyledSelect>
            </InputField>
          ) : null}
          <InputField>
            <StyledLabel>{getChargeLabel()}</StyledLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={amount ?? 0}
              onChange={e => {
                setAmount(Number(e.target.value));
              }}
            />
          </InputField>
          <InputField>
            <StyledLabel>Plan Details</StyledLabel>
            <StyledArea
              value={description}
              onChange={e => {
                e.stopPropagation();
                setDescription(e.target.value);
              }}
            />
          </InputField>

          <StyledButton onClick={handleSave} disabled={isCreating || isUpdating}>
            Save
          </StyledButton>
        </InputContainer>
      ) : null}
    </>
  );
};
